/**
 * ScoreScaleRowContainer — public entry for the ScoreScaleRow molecule
 * (SE-6980).
 *
 * The row is controlled: the owning feature holds the score and decides what a
 * selection writes. What the Container owns is the binding — one stable press
 * handler per score, so the View can stay a pure arrow with no behaviour in it
 * and no new function is minted per pill per render.
 * @module components/molecules/ScoreScaleRow/ScoreScaleRowContainer
 */
import React, { memo, useMemo, type ReactNode } from "react";

import type { ScoreScaleValue } from "./ScoreScaleRowView";
import ScoreScaleRowView, { SCORE_SCALE_VALUES } from "./ScoreScaleRowView";

/** Public props for the ScoreScaleRow molecule. */
export interface ScoreScaleRowProps {
  /** Row label, e.g. "Performance" or "Potential". */
  readonly label: string;
  /**
   * The selected score, or `null` when nothing has been chosen yet. `0` and
   * `null` are different states — see the View's module docs.
   */
  readonly value: number | null;
  /** Fired with the pressed score. Never fired with `null`. */
  readonly onSelect: (value: ScoreScaleValue) => void;
  /** Replaces the rendered label, when the consumer has an overlay for it (SE-7551). */
  readonly labelSlot?: ReactNode;
  /** Disables every pill in the row. */
  readonly disabled?: boolean;
  /**
   * Draw the web editor's 32x32 tiles rather than the phone's 48x44.
   *
   * Both sizes are the design's own — frame 32's `Btn/0` is 48x44, frames
   * 12/29's `RatingPill` is 32x32 — so this is a surface choice, not a
   * preference. See ScoreScaleRowView.
   */
  readonly isCompact?: boolean;
  /**
   * Test identity for the row. Each pill derives its own id by appending the
   * score, e.g. `MATCH_REPORT.PERFORMANCE_SCALE.7`.
   */
  readonly testID?: string;
}

/**
 * Binds one handler per score and renders the ScoreScaleRow view.
 * @param root0 - Component props
 * @param root0.label - Row label
 * @param root0.value - Selected score, or null when unset
 * @param root0.labelSlot - Replaces the rendered label
 * @param root0.onSelect - Selection handler
 * @param root0.disabled - Disables every pill
 * @param root0.isCompact - Draw the 32x32 web tiles rather than 48x44
 * @param root0.testID - Test identity for the row
 * @returns The scale row element.
 */
const ScoreScaleRowContainer = ({
  label,
  value,
  onSelect,
  labelSlot,
  disabled = false,
  isCompact = false,
  testID,
}: ScoreScaleRowProps) => {
  const pressHandlers = useMemo(
    () => SCORE_SCALE_VALUES.map(score => () => onSelect(score)),
    [onSelect]
  );

  return (
    <ScoreScaleRowView
      label={label}
      value={value}
      pressHandlers={pressHandlers}
      labelSlot={labelSlot}
      disabled={disabled}
      isCompact={isCompact}
      testID={testID}
    />
  );
};

ScoreScaleRowContainer.displayName = "ScoreScaleRowContainer";

export default memo(ScoreScaleRowContainer);
